/**
 * Подписка глазами владельца заведения.
 *
 * Это единственный экран, где клиент отдаёт нам деньги, поэтому
 * порядок на нём обратный привычному: сначала состояние одной фразой,
 * потом оплата, и только затем подробности. Раньше «Куда платить»
 * стояло под четырьмя равными карточками, а «Я оплатил» — в самом
 * низу, после настроек зала.
 *
 * Суммы и скидки берём из ответа сервера как есть: владелец платформы
 * меняет их у себя, и считать по-своему нельзя — клиент увидит одно,
 * а заплатит другое.
 *
 * QR рисует сервер, картинка приходит ссылкой: заведение может
 * работать без интернета, тянуть библиотеку неоткуда.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API = '/api/v1/subscription';

type Pay = {
  url: string | null; phone: string | null; name: string | null; note: string | null;
  /** ссылка на картинку QR: клиент платит сам, минуя партнёра */
  qr?: string | null;
};

type Period = { months: number; pct: number; amount: number; save: number };

type Known = {
  known: true;
  status: 'SETUP' | 'PENDING_PAYMENT' | 'ACTIVE' | 'EXPIRED' | 'SUSPENDED';
  planName: string; planPrice: number;
  paidUntil: string | null; daysLeft: number | null;
  monthly?: number; extraDue?: number;
  periods?: Period[];
  lines?: { title: string; kind: string; qty: number;
    unitPrice: number; sum: number; since: string }[];
  /** Прошлые платежи с оплаченными отрезками — те же, что видит платформа. */
  history?: {
    id?: string; amount: number; months: number; status: string;
    at?: string | null; from: string | null; to: string | null;
    comment?: string | null;
  }[];
  soon: boolean; urgent: boolean;
  limits: {
    maxPos: number | null; maxKds: number | null; maxWaiter: number | null;
    maxCouriers?: number | null;
    /** Сколько уже занято — приходит, если сервер умеет; без него просто не показываем. */
    used?: { pos?: number; kds?: number; waiter?: number; courier?: number } | null;
  };
  pay: Pay | null;
};

type Status = { known: false } | Known;

const money = (v: number) => `${Math.round(v / 100).toLocaleString('ru-RU')} ₸`;
const date = (s: string | null) => (s ? new Date(s).toLocaleDateString('ru-RU') : '—');
const longDate = (s: string | null) =>
  (s ? new Date(s).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : '—');

const days = (n: number): string => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return 'дней';
  if (b > 1 && b < 5) return 'дня';
  if (b === 1) return 'день';
  return 'дней';
};

/** Одна фраза о состоянии — то, ради чего человек открыл экран. */
function StateHead({ data }: { data: Known }) {
  const left = data.daysLeft;
  const late = left !== null && left < 0;
  const tone = late || data.status === 'EXPIRED' ? 'bad'
    : data.urgent || data.soon ? 'warn'
      : data.status === 'PENDING_PAYMENT' ? 'wait' : 'ok';

  const title = data.status === 'EXPIRED' || late
    ? `Просрочено ${Math.abs(left ?? 0)} ${days(left ?? 0)}`
    : data.paidUntil
      ? `Оплачено до ${longDate(data.paidUntil)}`
      : 'Срок ещё не начат';

  const note = late || data.status === 'EXPIRED'
    ? 'Касса работает, офис открыт только на чтение — отчёты и настройки недоступны'
    : left !== null
      ? `Осталось ${left} ${days(left)} · тариф «${data.planName}» · ${money(data.monthly ?? data.planPrice)} в месяц`
      : `Тариф «${data.planName}» · ${money(data.monthly ?? data.planPrice)} в месяц`;

  return (
    <div className={`sub-state ${tone}`}>
      <div>
        <h2>{title}</h2>
        <p>{note}</p>
      </div>
      {data.status === 'PENDING_PAYMENT' && (
        <span className="sub-chip">Оплата на проверке</span>
      )}
    </div>
  );
}

/** Значение с копированием: реквизиты чаще диктуют, чем набирают. */
function CopyLine({ label, value }: { label: string; value: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="sub-copy">
      <div>
        <span>{label}</span>
        <b>{value}</b>
      </div>
      <button className="btn small" onClick={() => {
        void navigator.clipboard?.writeText(value);
        setDone(true);
        window.setTimeout(() => setDone(false), 1600);
      }}>{done ? 'Скопировано' : 'Копировать'}</button>
    </div>
  );
}

/*
 * Устройства: что включено и что просить.
 *
 * Одно устройство каждого вида входит в основу, всё сверх — платно.
 * Ограничены именно устройства, а не люди: сотрудников заводят
 * сколько угодно, они входят по своим PIN хоть по очереди на одном
 * планшете. Раньше строка «официанты 3» читалась как запрет нанимать
 * четвёртого, и владельцы пугались.
 *
 * Цену клиент не называет: он просит, платформа одобряет и ставит
 * цену по своему прайсу. Поэтому в форме только вид, количество и
 * «зачем» — три системных окошка, где вид угадывался по тексту
 * («планшет» превращался в кассу), отсюда убраны.
 */
/** Какая строка счёта за какое устройство — связь видна в обе стороны. */
const DEVICE_LINE: Record<string, string> = {
  POS: 'касса', KDS: 'экран кухни', WAITER: 'телефон официанта',
};

const DEVICE_KINDS: { kind: 'POS' | 'KDS' | 'WAITER'; title: string; what: string }[] = [
  { kind: 'POS', title: 'Касса', what: 'приём заказов и оплата' },
  { kind: 'KDS', title: 'Экран кухни', what: 'заказы на кухне' },
  { kind: 'WAITER', title: 'Телефон официанта', what: 'заказ прямо у стола' },
];

function DeviceRequest({ token, limits }: {
  token: string;
  limits: Known['limits'];
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'POS' | 'KDS' | 'WAITER'>('POS');
  const [qty, setQty] = useState(1);
  const [why, setWhy] = useState('');
  const [done, setDone] = useState<'ok' | 'pending' | 'fail' | null>(null);

  const send = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API}/subscription/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          kind: 'DEVICE_LIMIT', deviceKind: kind, qty,
          reason: why.trim(),
        }),
      });
      return r.json() as Promise<{ ok?: boolean; code?: string }>;
    },
    onSuccess: (d) => {
      setDone(d?.ok ? 'ok' : d?.code === 'ALREADY_PENDING' ? 'pending' : 'fail');
      if (d?.ok) { setOpen(false); setWhy(''); setQty(1); }
    },
    onError: () => setDone('fail'),
  });

  /* Показываем только те виды, что реально есть. Курьерских терминалов
     в системе нет — доставка ведётся из кассы, и строка «Курьер 0» читалась
     как «можно попросить». Просить устройство, которого не бывает, бессмысленно. */
  const rows = [
    { title: 'Касса', limit: limits.maxPos, used: limits.used?.pos },
    { title: 'Экран кухни', limit: limits.maxKds, used: limits.used?.kds },
    { title: 'Телефон официанта', limit: limits.maxWaiter, used: limits.used?.waiter },
  ].filter((r) => r.limit === null || r.limit > 0 || (r.used ?? 0) > 0);

  return (
    <div className="sub-devices">
      <div className="sub-dev-head">
        <div>
          <h3>Устройства</h3>
          <p className="hint">
            По одному устройству каждого вида входит в тариф, остальные — платно.
            Считаются именно устройства: сотрудников заводите сколько нужно,
            они входят по своим PIN хоть по очереди на одном планшете.
          </p>
        </div>
        {!open && (
          <button className="btn" onClick={() => { setOpen(true); setDone(null); }}>
            Нужно ещё устройство
          </button>
        )}
      </div>

      <div className="sub-dev-grid">
        {rows.map((r) => {
          /* Клиент видел два числа и не понимал, за что платит.
             Пишем словами: одно в тарифе, остальные — платные. */
          const used = typeof r.used === 'number' ? r.used : null;
          const paid = used === null ? null : Math.max(0, used - 1);
          return (
            <div className="sub-dev-cell" key={r.title}>
              <span>{r.title}</span>
              <b>{used === null ? `${r.limit ?? '∞'}` : `${used} из ${r.limit ?? '∞'}`}</b>
              {used !== null && (
                <i className={paid && paid > 0 ? 'paid' : ''}>
                  {used === 0 ? 'ни одного не подключено'
                    : paid === 0 ? 'входит в тариф'
                      : `одно в тарифе, ${paid} платн${paid === 1 ? 'ое' : 'ых'}`}
                </i>
              )}
              {used !== null && r.limit !== null && used < r.limit && (
                <span className="sub-dev-free">можно подключить ещё {r.limit - used}</span>
              )}
            </div>
          );
        })}
      </div>

      {done === 'ok' && (
        <p className="sub-dev-ok">
          Заявка отправлена. Платформа назовёт стоимость и подключит —
          после одобрения устройству понадобится код активации, его выдаст тот, кто одобрил.
        </p>
      )}
      {done === 'pending' && (
        <p className="sub-dev-ok">Заявка уже отправлена и ждёт ответа — новая не нужна.</p>
      )}
      {done === 'fail' && (
        <p className="rb-warn">Не получилось отправить. Попробуйте ещё раз или позвоните тому, кто вас подключал.</p>
      )}

      {open && (
        <div className="sub-dev-form">
          <label>Что нужно
            <select value={kind} onChange={(e) => setKind(e.target.value as 'POS')}>
              {DEVICE_KINDS.map((d) => (
                <option key={d.kind} value={d.kind}>{d.title} — {d.what}</option>
              ))}
            </select>
          </label>
          <label>Сколько
            <input type="number" min={1} max={20} value={qty}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} />
          </label>
          <label className="sub-dev-why">Зачем и когда нужно
            <input value={why} placeholder="Открываем летнюю веранду, нужна касса к 1 июня"
              onChange={(e) => setWhy(e.target.value)} />
          </label>
          <div className="sub-dev-actions">
            <button className="btn" onClick={() => { setOpen(false); setDone(null); }}>Отмена</button>
            <button className="btn primary" disabled={why.trim().length < 5 || send.isPending}
              onClick={() => send.mutate()}>
              {send.isPending ? 'Отправляем…' : 'Отправить заявку'}
            </button>
          </div>
          <p className="hint">
            Цену назовёт платформа по своему прайсу — вводить её здесь не нужно.
            Одобрят: устройство появится в счёте со дня подключения, а если до конца
            оплаченного периода меньше десяти дней, доплату за остаток не возьмём.
          </p>
        </div>
      )}
    </div>
  );
}

/*
 * Предупреждение на всех экранах офиса.
 *
 * Раньше о просрочке узнавали, когда закрывался офис. Теперь за три
 * дня и за день висит полоса со ссылкой на оплату.
 *
 * Запрос тот же, что у экрана подписки — ключ общий, лишнего обращения
 * нет. Закрыть можно на день и только пока срок не вышел: просрочку
 * прятать нельзя — это уже не напоминание, а состояние заведения.
 */
function useSubscription(token: string) {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: async (): Promise<Status> => {
      const r = await fetch(API, { headers: { Authorization: `Bearer ${token}` } });
      return r.json() as Promise<Status>;
    },
  });
}

const HIDDEN_KEY = 'dastarhan.sub.banner.hidden';

export function SubscriptionBanner({ token, onOpen }: { token: string; onOpen: () => void }) {
  const { data } = useSubscription(token);
  const today = new Date().toISOString().slice(0, 10);
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDDEN_KEY) === today);

  if (!data?.known) return null;
  const left = data.daysLeft;
  const late = data.status === 'EXPIRED' || (left !== null && left < 0);
  const near = left !== null && left >= 0 && left <= 3;
  if (!late && !near) return null;
  if (near && hidden) return null;

  return (
    <div className={`sub-banner ${late ? 'bad' : 'warn'}`} role="status">
      <b>
        {late
          ? `Подписка просрочена на ${Math.abs(left ?? 0)} ${days(left ?? 0)}`
          : left === 0
            ? 'Подписка заканчивается сегодня'
            : `Подписка заканчивается через ${left} ${days(left ?? 0)}`}
      </b>
      <span>
        {late
          ? 'Касса работает, офис открыт только на чтение'
          : 'После окончания офис откроется только на чтение — касса продолжит работать'}
      </span>
      <button className="btn" onClick={onOpen}>Оплатить</button>
      {!late && (
        <button
          className="sub-banner-x"
          aria-label="Скрыть до завтра"
          onClick={() => { localStorage.setItem(HIDDEN_KEY, today); setHidden(true); }}
        >×</button>
      )}
    </div>
  );
}

/** Точка у пункта меню: глаз цепляет раньше, чем человек читает. */
export function SubscriptionDot({ token }: { token: string }) {
  const { data } = useSubscription(token);
  if (!data?.known) return null;
  const left = data.daysLeft;
  const late = data.status === 'EXPIRED' || (left !== null && left < 0);
  const near = left !== null && left >= 0 && left <= 3;
  if (!late && !near) return null;
  return <span className={`sub-dot ${late ? 'bad' : 'warn'}`} aria-hidden="true" />;
}

export function SubscriptionScreen({ token }: { token: string }) {
  const qc = useQueryClient();
  const [months, setMonths] = useState(1);
  const [comment, setComment] = useState('');
  const [justSent, setJustSent] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const { data, isPending } = useSubscription(token);

  const declare = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API}/subscription/declare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ months, comment }),
      });
      return r.json();
    },
    onSuccess: () => {
      setJustSent(true);
      void qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  if (isPending) {
    return (
      <section className="sub-screen">
        <div className="sub-skeleton" aria-hidden="true">
          <span className="sk-bar tall" />
          <span className="sk-bar" />
          <span className="sk-bar wide" />
        </div>
      </section>
    );
  }

  if (!data?.known) {
    return (
      <section className="sub-screen">
        <div className="sub-state">
          <div>
            <h2>Подписка ведётся вне кабинета</h2>
            <p>
              Заведение подключали до того, как появился этот раздел, поэтому срок
              и счёт здесь не показываются. На работу это не влияет: касса, кухня
              и отчёты работают как обычно.
            </p>
          </div>
        </div>
        <div className="sub-lines">
          <h3>Что делать</h3>
          <p className="hint">
            Срок и оплату ведёт тот, кто вас подключал, — напишите ему, когда подойдёт
            время платить. Хотите платить напрямую и видеть счёт здесь — попросите
            перевести заведение на новый порядок подписки.
          </p>
        </div>
        <hr className="sub-sep" />
        <ServiceCharge token={token} />
      </section>
    );
  }

  const perMonth = data.monthly ?? data.planPrice;
  const periods: Period[] = data.periods?.length
    ? data.periods
    : [{ months: 1, pct: 0, amount: perMonth, save: 0 }];
  const chosen = periods.find((p) => p.months === months) ?? periods[0];
  const extra = data.extraDue ?? 0;
  /* Итог — ровно то, что прислал сервер за выбранный срок, плюс разовая
     доплата за устройства середины периода. Свою арифметику здесь не
     заводим: скидки живут в настройках платформы. */
  const total = (chosen?.amount ?? perMonth) + extra;

  /* Заявка уже висит: и сразу после нажатия, и после перезагрузки. */
  const waiting = justSent || data.status === 'PENDING_PAYMENT';

  /* Срок далеко и всё оплачено — человек пришёл смотреть счёт, а не платить:
     QR и реквизиты прячем за одну кнопку. Заранее платят редко, но платят. */
  /* Платить нечем: платформа не завела ни ссылки, ни QR, ни номера. */
  const hasWay = !!(data.pay?.url || data.pay?.qr || data.pay?.phone);
  const calm = data.status === 'ACTIVE' && (data.daysLeft ?? 0) > 7 && !waiting;
  const showPay = payOpen || !calm;

  return (
    <section className="sub-screen">
      <StateHead data={data} />

      {/* ── Оплата: первым делом, а не под настройками зала ── */}
      {!showPay && (
        <div className="sub-calm">
          <div>
            <b>Всё оплачено</b>
            <p>
              Следующий платёж {money(perMonth)} — {longDate(data.paidUntil)}.
              Напомним за три дня.
            </p>
          </div>
          <button className="btn" onClick={() => setPayOpen(true)}>Оплатить заранее</button>
        </div>
      )}

      {showPay && (
      <div className="sub-pay">
        <div className="sub-pay-main">
          <h3>Оплата подписки</h3>

          {/* Шаг 1 — срок. Суммы и проценты берём из ответа сервера как есть. */}
          <div className="sub-step">
            <span className="sub-step-n">1</span>
            <div className="sub-step-body">
              <b>Выберите срок</b>
              <div className="sub-periods" role="radiogroup" aria-label="За какой срок платите">
                {periods.map((p) => (
                  <button
                    key={p.months}
                    role="radio"
                    aria-checked={p.months === months}
                    className={`sub-period ${p.months === months ? 'on' : ''}`}
                    onClick={() => setMonths(p.months)}
                  >
                    <b>{p.months} мес.</b>
                    <span className="sub-period-sum">{money(p.amount)}</span>
                    {p.pct > 0
                      ? <i className="sub-period-save">−{p.pct}% · экономия {money(p.save)}</i>
                      : <i className="sub-period-save quiet">без скидки</i>}
                  </button>
                ))}
              </div>
              {extra > 0 && (
                <p className="sub-extra">
                  Плюс разовая доплата {money(extra)} за устройства, подключённые в середине
                  оплаченного периода — чтобы у вас осталась одна дата платежа.
                </p>
              )}
            </div>
          </div>

          {/* Шаг 2 — сумма и путь оплаты. */}
          <div className="sub-step">
            <span className="sub-step-n">2</span>
            <div className="sub-step-body">
              <b>Заплатите {money(total)}</b>
              <div className="sub-total">
                <span>К оплате за {months} мес.</span>
                <b>{money(total)}</b>
              </div>

              {hasWay ? (
                <>
                  {data.pay?.url && (
                    <a className="btn accent sub-paybtn" href={data.pay.url} target="_blank" rel="noreferrer">
                      Оплатить {money(total)}
                    </a>
                  )}

                  {data.pay?.phone && (
                    <details className="sub-manual">
                      <summary>Перевести по номеру</summary>
                      <div className="sub-req">
                        <CopyLine label="Номер" value={data.pay.phone} />
                        {data.pay.name && <CopyLine label="Получатель" value={data.pay.name} />}
                        {data.pay.note && <CopyLine label="В комментарии" value={data.pay.note} />}
                        {data.pay.url && <CopyLine label="Ссылка на оплату" value={data.pay.url} />}
                      </div>
                    </details>
                  )}
                </>
              ) : (
                <div className="sub-noway">
                  <b>Реквизиты не настроены</b>
                  <p>
                    Платформа ещё не указала, куда платить. Свяжитесь с нами — подскажем
                    и продлим доступ вручную, чтобы работа не встала.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Шаг 3 — отметка. Стоит рядом с оплатой, а не отдельной карточкой. */}
          <div className="sub-step">
            <span className="sub-step-n">3</span>
            <div className="sub-step-body">
              <b>Отметьте, что заплатили</b>
              <p className="hint">
                Мы проверим поступление и продлим доступ — обычно в течение дня.
                Кнопка ниже.
              </p>
            </div>
          </div>
        </div>

        {data.pay?.qr && (
          <figure className="sub-qr">
            <img src={data.pay.qr} alt="QR для оплаты" />
            <figcaption>
              <b>QR на {money(total)}</b>
              Наведите камеру телефона — оплата уйдёт напрямую платформе.
              Если в QR зашита своя сумма, проверьте её перед оплатой.
            </figcaption>
          </figure>
        )}
      </div>
      )}

      {/* ── Отметка об оплате ── */}
      {waiting ? (
        <div className="sub-sent">
          <b>Мы уже получили вашу отметку и проверяем</b>
          <p>
            Обычно подтверждаем в течение дня. Доступ продлится сам —
            повторно отмечать не нужно.
          </p>
        </div>
      ) : showPay ? (
        <div className="sub-declare">
          <div>
            <b>Заплатили?</b>
            <p>Отметьте — мы проверим и продлим доступ, обычно в течение дня.</p>
          </div>
          <input
            value={comment}
            placeholder="Номер платежа или дата — если есть"
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            className="btn primary sub-declare-btn"
            disabled={declare.isPending}
            onClick={() => declare.mutate()}
          >
            {declare.isPending ? 'Отправляем…' : `Я оплатил ${money(total)}`}
          </button>
        </div>
      ) : null}

      {/* ── За что платите ── */}
      {(data.lines?.length ?? 0) > 0 && (
        <div className="sub-lines">
          <h3>За что платите</h3>
          <table>
            <tbody>
              {(data.lines ?? []).map((l, i) => (
                <tr key={i}>
                  <td>
                    {l.title}
                    {l.qty > 1 && <span className="sub-qty"> × {l.qty}</span>}
                    <div className="sub-since">
                      с {date(l.since)}
                      {DEVICE_LINE[l.kind] ? ` · ${DEVICE_LINE[l.kind]} сверх тарифа` : ''}
                    </div>
                  </td>
                  <td className="sub-sum">{money(l.sum)}</td>
                </tr>
              ))}
              <tr className="sub-line-total">
                <td>В месяц</td>
                <td className="sub-sum">{money(perMonth)}</td>
              </tr>
            </tbody>
          </table>
          {extra > 0 && (
            <p className="hint">
              Доплата за середину месяца считается по дням до вашей общей даты платежа:
              подключили устройство в середине периода — платите только за оставшиеся дни,
              дальше оно идёт в обычном месячном счёте. Если до конца периода меньше
              десяти дней, доплату не берём вовсе.
            </p>
          )}
        </div>
      )}

      {/* ── История оплат: за что и когда списали ── */}
      {(data.history?.length ?? 0) > 0 && (
        <div className="sub-history">
          <h3>История оплат</h3>
          <table>
            <tbody>
              {(data.history ?? []).map((h, i) => (
                <tr key={h.id ?? i}>
                  <td>
                    {h.from && h.to
                      ? <b className="sub-period-range">{date(h.from)} → {date(h.to)}</b>
                      : <b className="sub-period-range none">отрезок ещё не занят</b>}
                    <div className="sub-since">
                      {h.months} мес.{h.comment ? ` · ${h.comment}` : ''}
                    </div>
                  </td>
                  <td className="sub-sum">{money(h.amount)}</td>
                  <td className="sub-hstatus">
                    {h.status === 'APPROVED' && <span className="ok">подтверждён</span>}
                    {h.status === 'PENDING' && <span className="wait">на проверке</span>}
                    {h.status === 'REJECTED' && <span className="bad">отклонён</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="hint">
            Каждый отрезок начинается там, где кончился предыдущий — оплата раньше срока
            не сжигает оставшиеся дни, а прибавляется к ним.
          </p>
        </div>
      )}

      {/* ── Устройства и заявка ── */}
      <DeviceRequest token={token} limits={data.limits} />

      {/* ── Пробный период: сказать заранее, что закроется ── */}
      {data.planName === 'Пробный период' && (
        <p className="sub-trial">
          Сейчас у вас пробный период — открыто всё, включая калькуляцию себестоимости
          и конструктор отчётов. После окончания эти два раздела закроются на тарифе
          «Базовый» — это не поломка. Остальное, включая кассу и отчёты, останется на месте.
        </p>
      )}

      <hr className="sub-sep" />

      <ServiceCharge token={token} />
    </section>
  );
}

/**
 * Сбор за обслуживание.
 *
 * К подписке отношения не имеет — это настройка зала, поэтому стоит
 * под разделителем, в самом низу, а не между оплатой и счётом.
 *
 * В ресторанах его берут, в столовых нет — поэтому по умолчанию ноль
 * и включает владелец. Три настройки, каждая из живой практики:
 * процент, «только в зале» (с собой сбор не берут) и «от скольких
 * гостей» (за одинокого гостя за столиком брать неловко, за компанию
 * из восьми — принято).
 */
function ServiceCharge({ token }: { token: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['acc-profile'],
    queryFn: async () => {
      const r = await fetch('/api/v1/account/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.json() as Promise<{
        servicePct?: number; serviceDineInOnly?: boolean; serviceFromGuests?: number;
      }>;
    },
  });

  const [f, setF] = useState<{ percent: number; dineInOnly: boolean; fromGuests: number } | null>(null);
  const cur = f ?? {
    percent: data?.servicePct ?? 0,
    dineInOnly: data?.serviceDineInOnly ?? true,
    fromGuests: data?.serviceFromGuests ?? 0,
  };

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/v1/account/service', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(cur),
      });
      if (!r.ok) throw new Error('нет прав');
      return r.json();
    },
    onSuccess: () => {
      setF(null);
      void qc.invalidateQueries({ queryKey: ['acc-profile'] });
    },
  });

  return (
    <div className="declare">
      <h3>Обслуживание</h3>
      <p className="hint">
        Надбавка к счёту за работу зала. Считается после скидки —
        гость платит процент с того, что реально отдаёт.
        Печатается в чеке отдельной строкой с процентом.
      </p>

      <div className="declare-row">
        <label>Процент
          <input type="number" min={0} max={30} value={cur.percent}
            onChange={(e) => setF({ ...cur, percent: Number(e.target.value) })} />
        </label>
        <label>От скольких гостей
          <input type="number" min={0} max={30} value={cur.fromGuests}
            onChange={(e) => setF({ ...cur, fromGuests: Number(e.target.value) })} />
        </label>
        <label className="check" style={{ alignSelf: 'center' }}>
          <input type="checkbox" checked={cur.dineInOnly}
            onChange={(e) => setF({ ...cur, dineInOnly: e.target.checked })} />
          Только в зале
        </label>
        <button className="btn primary" disabled={!f || save.isPending}
          onClick={() => save.mutate()}>
          {f ? 'Сохранить' : 'Сохранено'}
        </button>
      </div>

      {cur.percent === 0 ? (
        <p className="hint">Сейчас сбор не берётся.</p>
      ) : (
        <p className="hint">
          К счёту 10 000 ₸ добавится {Math.round(10000 * cur.percent / 100).toLocaleString('ru-RU')} ₸
          {cur.dineInOnly ? ' — только для заказов в зале' : ''}
          {cur.fromGuests > 0 ? `, от ${cur.fromGuests} гостей` : ''}.
        </p>
      )}
      {save.isError && <p className="rb-warn">Менять это может только владелец.</p>}
    </div>
  );
}
